import type { Atom } from "../types/Atom"
import { BigIntToUint8 } from "./BigIntToUint8"
import { BooleanToUint8 } from "./BooleanToUint8"
import { NumberToUint8 } from "./NumberToUint8"
import { StringToUInt8 } from "./StringToUInt8"

/**
 * Converts an atomic value (string, number, boolean, or bigint) to a Uint8Array.
 * Automatically selects the appropriate conversion method based on value type.
 *
 * @param value - The atomic value to convert
 * @returns A Uint8Array representation of the value
 *
 * @example
 * ```ts
 * import { AtomToUint8 } from "@pdfking/tfunc"
 *
 * // String
 * const strBytes = AtomToUint8("hello");
 *
 * // Number
 * const numBytes = AtomToUint8(42);
 *
 * // Boolean
 * const boolBytes = AtomToUint8(true);
 *
 * // BigInt
 * const bigintBytes = AtomToUint8(9007199254740991n);
 *
 * // Use for hashing mixed types
 * const hash = await UInt8Hash([
 *   AtomToUint8("user"),
 *   AtomToUint8(123),
 *   AtomToUint8(true)
 * ]);
 * ```
 */
export const AtomToUint8 = (value: Atom): Uint8Array => {
  switch (typeof value) {
    case "bigint":
      return BigIntToUint8(value)
    case "boolean":
      return BooleanToUint8(value)
    case "number":
      return NumberToUint8(value)
    case "string":
      return StringToUInt8(value)
  }
}
